<template>
  <div>
    <label class="block rounded-xxs border-solid p-m cursor-pointer w-full m-b-sm"
           :class="mode === 'rosters' ? 'border-2 border-green-600 bg-green-50' : 'border border-gray-200'"
           style="font-weight: normal;">
      <span class="flex align-items-start gap-x-sm">
        <input type="radio" name="check-in-mode" value="rosters" :checked="mode === 'rosters'"
               @change="setMode('rosters')" class="m-t-xxs" data-cy="check-in-mode-rosters">
        <span class="flex-1">
          <!-- Badge sits above the title on mobile, at the far right of it on desktop -->
          <span class="flex flex-col flex-row-lg justify-space-between align-items-start gap-y-xxs gap-x-sm">
            <span class="text-10 font-heavy text-green-700 bg-green-100 rounded-xxs p-x-xxs p-y-xxs order-last-lg">RECOMMENDED</span>
            <span class="text-14 font-semi text-gray-900">{{ isPractice ? 'Only wrestlers on these rosters' : 'Specific rosters' }}</span>
          </span>
          <span v-if="isPractice" class="block text-14 text-gray-500 m-t-xxs">Check-in and attendance are limited to these rosters.</span>
          <span v-else-if="isPrivateLesson" class="block text-14 text-gray-500 m-t-xxs">Self-booking is limited to these rosters.</span>
          <span v-else class="block text-14 text-gray-500 m-t-xxs">They'll see it as theirs; check-in and attendance follow.</span>
        </span>
      </span>
    </label>

    <div v-if="mode === 'rosters'" class="m-b-sm">
      <div class="flex flex-wrap gap-xxs m-b-xs">
        <span v-if="notDestroyedRosterEvents.length === 0" class="text-14 text-gray-400">No rosters linked yet</span>
        <button v-for="er in notDestroyedRosterEvents" :key="`linked-${er.roster.id}`" type="button"
                @click="$emit('toggle-roster', er.roster)"
                class="btn btn-success btn-outline rounded-lg font-small" style="padding: 2px 6px;">
          {{ er.roster.name }}<span v-if="er.roster.wrestler_count != null"> ({{ er.roster.wrestler_count }})</span>
          <i class="fa-solid fa-xmark"></i>
        </button>
        <button v-for="er in destroyedRosterEvents" :key="`removed-${er.roster.id}`" type="button"
                @click="$emit('toggle-roster', er.roster)"
                class="btn btn-danger btn-outline rounded-lg font-small" style="padding: 2px 6px;">
          {{ er.roster.name }} (will be removed)
          <i class="fa-solid fa-xmark"></i>
        </button>
      </div>
      <search
          class="search-form"
          response-key="rosters"
          placeholder="Type the name of a roster..."
          :mutate-history="false"
          :allow-add="false"
          only-dismiss-model="Roster"
          :small-input="true"
          :display-all-on-empty="false"
          readable-model-name="roster"
          base-url="/api/v1/rosters?q[archived_eq]=false">
        <template v-slot:list="slotProps">
          <div
              class="border-top m-t-sm m-b-xs p-t-sm p-b-xs cursor-pointer flex justify-space-between align-items-center p-r-sm"
              @click="$emit('toggle-roster', slotProps.result)">
            <div>
              {{ slotProps.result.name }}
              <span class="text-gray-400 text-12">{{ wrestlerCountLabel(slotProps.result) }}</span>
            </div>
            <div class="border rounded-sm border-blue-200 border-solid font-small" style="padding: 2px 6px;">
              Link
            </div>
          </div>
        </template>
      </search>
      <p v-if="consequence" class="text-14 m-t-sm font-semi" :class="consequence.cssClass" data-cy="check-in-consequence">{{ consequence.message }}</p>
    </div>

    <label class="block rounded-xxs border-solid p-m w-full m-b-sm"
           :class="[anyoneDisabled ? 'border border-gray-200 bg-gray-50' : (mode === 'anyone' ? 'border-2 border-gray-400 bg-gray-50 cursor-pointer' : 'border border-gray-200 cursor-pointer')]"
           style="font-weight: normal;">
      <span class="flex align-items-start gap-x-sm">
        <input type="radio" name="check-in-mode" value="anyone" :checked="mode === 'anyone'" :disabled="anyoneDisabled"
               @change="setMode('anyone')" class="m-t-xxs" data-cy="check-in-mode-anyone">
        <span>
          <span class="block text-14 font-semi" :class="anyoneDisabled ? 'text-gray-400' : 'text-gray-900'">{{ isPractice ? 'Anyone on the team' : 'The whole team' }}</span>
          <span v-if="anyoneDisabled" class="block text-14 text-gray-500 m-t-xxs">
            Practices require at least one roster. For open mats or informal events, use the <em>Other</em> event type.
          </span>
          <span v-else class="block text-14 text-gray-500 m-t-xxs">
            {{ isPrivateLesson ? 'Everyone sees it; any wrestler can book it.' : 'Everyone sees it; any wrestler can check in.' }}
          </span>
        </span>
      </span>
    </label>
    <p v-if="mode === 'anyone' && !anyoneDisabled" class="text-14 font-semi text-gray-600" data-cy="check-in-consequence-anyone">
      {{ isPrivateLesson ? 'Everyone on the team will see this lesson and can book it.' : 'Everyone on the team will see this event and can check in.' }}
    </p>
    <p v-if="isPrivateLesson" class="text-12 text-gray-400 m-t-xs">
      <i class="fa-regular fa-lightbulb text-yellow-600"></i>&nbsp;You can always manually add wrestlers to a private lesson booking &mdash; rosters only affect who can self-book.
    </p>
    <p v-else class="text-12 text-gray-400 m-t-xs">
      <i class="fa-regular fa-lightbulb text-yellow-600"></i>&nbsp;Linked rosters also let families filter their WIQ calendar to their wrestler's events.
    </p>
  </div>
</template>

<script>
import Search from '../../shared/search.vue';

// Shared "Who can check in?" section: radio between roster-restricted and
// open-to-the-team check-in access, with consequence messaging that spells out
// what the choice means at the kiosk. The parent owns the rosterEvents map
// (same shape event_create.vue has always used: keyed by roster id, with
// optional persisted id and a _destroy flag) and the mode; this component
// only emits `set-mode` and `toggle-roster`.
export default {
  name: 'check-in-access',
  components: {
    Search,
  },
  props: {
    eventType: {
      type: String,
      required: false,
      default: '',
    },
    // 'rosters' | 'anyone'
    mode: {
      type: String,
      required: true,
    },
    rosterEvents: {
      type: Object,
      required: true,
    },
  },
  computed: {
    notDestroyedRosterEvents() {
      return _.flatMap(this.rosterEvents).filter((re) => !re._destroy);
    },
    destroyedRosterEvents() {
      return _.flatMap(this.rosterEvents).filter((re) => re._destroy);
    },
    isPractice() {
      return this.eventType === 'practice';
    },
    // Private lessons flip the framing: rosters gate who can self-book the
    // lesson, not who can check in (coaches can always add bookings manually).
    isPrivateLesson() {
      return this.eventType === 'private_lesson';
    },
    // Practices must keep at least one roster, so the "anyone" escape hatch is
    // shown but disabled with copy pointing at the Other event type.
    anyoneDisabled() {
      // Still block *switching to* anyone for practices, but don't fight a
      // legacy roster-less practice that already loaded in this mode (which
      // event_create.vue initializes as 'anyone') — otherwise the radio renders
      // checked and disabled at the same time.
      return this.isPractice && this.mode !== 'anyone';
    },
    // Practices get the hard check-in-gate framing; for every other event type
    // roster linkage is primarily "these rosters should know about this", with
    // the check-in list as a side effect — so the tone stays neutral.
    consequence() {
      const vm = this;
      const linked = vm.notDestroyedRosterEvents;
      if (linked.length === 0) {
        // Practices already force a roster (disabled "anyone" radio + the step
        // gate), so the nag would be noise
        if (vm.isPractice) {
          return null;
        }
        return {
          cssClass: 'text-gray-500',
          message: vm.isPrivateLesson
            ? 'No rosters linked — anyone on the team can book this lesson.'
            : 'No rosters linked — the whole team will see this event.',
        };
      }

      const names = linked.map((re) => re.roster.name).join(', ');
      const counts = linked.map((re) => re.roster.wrestler_count).filter((c) => c != null);
      // Wrestler counts are only known when rosters were picked in this session
      // (search results include them; a previously saved event's roster_events
      // don't), so degrade to names-only when any count is missing.
      const haveCounts = counts.length === linked.length;
      const total = counts.reduce((sum, c) => sum + c, 0);

      if (vm.isPractice) {
        if (haveCounts) {
          return {
            cssClass: 'text-green-700',
            message: `✓ ${total} wrestler${total === 1 ? '' : 's'} (${names}) can check in.`,
          };
        }
        return {
          cssClass: 'text-green-700',
          message: `✓ Only wrestlers on ${names} can check in.`,
        };
      }

      if (vm.isPrivateLesson) {
        if (haveCounts) {
          return {
            cssClass: 'text-green-700',
            message: `✓ ${total} wrestler${total === 1 ? '' : 's'} (${names}) can self-book.`,
          };
        }
        return {
          cssClass: 'text-green-700',
          message: `✓ Only wrestlers on ${names} can self-book.`,
        };
      }

      if (haveCounts) {
        return {
          cssClass: 'text-green-700',
          message: `✓ For ${names} — ${total} wrestler${total === 1 ? '' : 's'}. They'll see it as theirs; check-in follows.`,
        };
      }
      return {
        cssClass: 'text-green-700',
        message: `✓ For ${names}. They'll see it as theirs; check-in follows.`,
      };
    },
  },
  methods: {
    setMode(mode) {
      this.$emit('set-mode', mode);
    },
    wrestlerCountLabel(roster) {
      const count = _.get(roster, 'stats.roster_memberships_count', null);
      if (count == null) {
        return '';
      }
      return `${count} wrestler${count === 1 ? '' : 's'}`;
    },
  },
};
</script>
